import {
  PRIORITY_LABELS,
  formatMoney,
  isHttpUrl,
  shareIntro,
  sharedSections,
  type SharedPiece,
  type SharedView,
} from '@closetly/core';
import { ClientResponseError } from 'pocketbase';
import { useCallback, useEffect, useState } from 'react';
import { APP_NAME } from '../lib/config';
import { storedImageUrl } from '../lib/image';
import { loadSharedView, myReservations, releasePiece, rememberReservation, reservePiece } from '../lib/shares';
import { useToast } from '../lib/toast';
import { Collage } from './Collage';
import { Sheet } from './Sheet';

type Load = { state: 'loading' } | { state: 'gone' } | { state: 'offline' } | { state: 'ok'; view: SharedView };

/** What a shared link shows to anyone who has it, signed in or not. */
export function SharePage({ token }: { token: string }) {
  const [load, setLoad] = useState<Load>({ state: 'loading' });
  const [mine, setMine] = useState(() => myReservations(token));
  const [reserving, setReserving] = useState<SharedPiece | null>(null);
  const toast = useToast();

  const refresh = useCallback(
    () =>
      loadSharedView(token)
        .then((view) => setLoad({ state: 'ok', view }))
        .catch((err) =>
          setLoad(err instanceof ClientResponseError && err.status === 404 ? { state: 'gone' } : { state: 'offline' }),
        ),
    [token],
  );
  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (load.state === 'ok') document.title = `${load.view.title} · ${APP_NAME}`;
  }, [load]);

  async function reserve(piece: SharedPiece, name: string) {
    setReserving(null);
    try {
      const { key } = await reservePiece(token, piece.id, name);
      rememberReservation(token, piece.id, key);
      setMine(myReservations(token));
      toast(`„${piece.name}“ ist für dich reserviert`);
    } catch (err) {
      toast(
        err instanceof ClientResponseError && err.status === 409
          ? 'Das hat gerade schon jemand reserviert.'
          : 'Das hat nicht geklappt. Versuch es nochmal.',
      );
    }
    await refresh();
  }

  async function release(piece: SharedPiece) {
    const key = mine[piece.id];
    if (!key) return;
    try {
      await releasePiece(token, piece.id, key);
      toast('Reservierung zurückgenommen');
    } catch (err) {
      if (!(err instanceof ClientResponseError && err.status === 404)) {
        toast('Das hat nicht geklappt. Versuch es nochmal.');
        return;
      }
    }
    rememberReservation(token, piece.id, null);
    setMine(myReservations(token));
    await refresh();
  }

  return (
    <div className="wrap share-page">
      <header className="share-top">
        <span className="brand">{APP_NAME}</span>
      </header>
      {load.state === 'loading' && <div className="loading">Lade…</div>}
      {load.state === 'gone' && (
        <div className="empty">
          <strong>Diesen Link gibt es nicht (mehr)</strong>
          Vielleicht wurde er gestoppt. Frag nach einem neuen.
        </div>
      )}
      {load.state === 'offline' && (
        <div className="empty">
          <strong>Keine Verbindung</strong>
          <button type="button" className="btn" onClick={() => void refresh()}>
            Nochmal versuchen
          </button>
        </div>
      )}
      {load.state === 'ok' && (
        <Shared view={load.view} mine={mine} onReserve={setReserving} onRelease={release} />
      )}
      <p className="foot">Geteilt mit {APP_NAME}, der Wunschliste für Kleidung.</p>
      {reserving && (
        <ReserveSheet piece={reserving} onClose={() => setReserving(null)} onReserve={(name) => reserve(reserving, name)} />
      )}
    </div>
  );
}

interface PieceActions {
  mine: Record<string, string>;
  onReserve: (p: SharedPiece) => void;
  onRelease: (p: SharedPiece) => void;
}

function Shared({ view, ...actions }: { view: SharedView } & PieceActions) {
  const s = sharedSections(view);
  const empty = s.outfits.length === 0 && s.singles.length === 0;
  return (
    <>
      <h1 className="share-title">{view.title}</h1>
      {view.owner_name && <p className="share-by">von {view.owner_name}</p>}
      {!empty && <p className="hint share-intro">{shareIntro(view)}</p>}
      {empty && (
        <div className="empty">
          <strong>Hier ist gerade nichts</strong>
        </div>
      )}

      {s.outfits.map(({ outfit, pieces }) => (
        <section key={outfit.id} className="detail shared-outfit" aria-label={outfit.name || 'Outfit'}>
          <div className="detail-visual">
            <Collage pieces={pieces} big />
          </div>
          <div className="detail-info">
            <h2>{outfit.name || 'Outfit'}</h2>
            {outfit.note && <p className="note">{outfit.note}</p>}
            <ul className="pieces">
              {pieces.map((p) => (
                <PieceRow key={p.id} piece={p} {...actions} />
              ))}
            </ul>
          </div>
        </section>
      ))}

      {s.singles.length > 0 && (
        <>
          {s.outfits.length > 0 && <h2 className="share-sub">Einzelteile</h2>}
          <div className="board">
            {s.singles.map((p) => (
              <SharedTile key={p.id} piece={p} {...actions} />
            ))}
          </div>
        </>
      )}
    </>
  );
}

const price = (p: SharedPiece) => (p.price_cents ? formatMoney(p.price_cents, p.currency || undefined) : '');
const details = (p: SharedPiece) => [p.shop, p.size && `Gr. ${p.size}`, p.color].filter(Boolean).join(' · ');

function ReserveButton({ piece: p, mine, onReserve, onRelease }: { piece: SharedPiece } & PieceActions) {
  if (p.bought) return <span className="state">Schon gekauft</span>;
  if (mine[p.id] && p.reserved)
    return (
      <span className="state mine">
        Reserviert von dir ·{' '}
        <button type="button" className="linkbtn" onClick={() => onRelease(p)}>
          Zurücknehmen
        </button>
      </span>
    );
  if (p.reserved) return <span className="state">Schon reserviert</span>;
  return (
    <button type="button" className="btn primary" onClick={() => onReserve(p)}>
      Reservieren
    </button>
  );
}

function ShopLink({ piece: p }: { piece: SharedPiece }) {
  if (!p.link || !isHttpUrl(p.link) || p.bought) return null;
  return (
    <a className="btn" href={p.link} target="_blank" rel="noopener noreferrer">
      Zum Shop ↗
    </a>
  );
}

function PieceRow({ piece: p, ...actions }: { piece: SharedPiece } & PieceActions) {
  const img = storedImageUrl(p);
  return (
    <li className={p.bought ? 'bought' : ''}>
      <div className="piece">
        {img ? <img src={img} alt="" loading="lazy" /> : <span className="piece-ph" aria-hidden="true" />}
        <span className="piece-text">
          <b>{p.name}</b>
          <small>{details(p)}</small>
        </span>
        <span className="piece-price">{price(p)}</span>
      </div>
      {p.note && <p className="note">{p.note}</p>}
      <div className="piece-actions">
        <ShopLink piece={p} />
        <ReserveButton piece={p} {...actions} />
      </div>
    </li>
  );
}

function SharedTile({ piece: p, ...actions }: { piece: SharedPiece } & PieceActions) {
  const img = storedImageUrl(p);
  return (
    <article className={`tile shared-tile${p.bought ? ' bought' : ''}`} aria-label={p.name}>
      <div className="tile-open">
        {img ? (
          <img src={img} alt="" loading="lazy" />
        ) : (
          <span className="tile-ph">
            <b>{p.name}</b>
            {p.category}
          </span>
        )}
        {p.priority === 1 && !p.bought && <span className="tile-badge drop">{PRIORITY_LABELS[1]}</span>}
      </div>
      <span className="tile-meta">
        <span className="tile-name">{p.name}</span>
        {price(p) && <span className="tile-price">{price(p)}</span>}
      </span>
      {details(p) && <span className="tile-details">{details(p)}</span>}
      {p.note && <p className="note">{p.note}</p>}
      <div className="tile-actions">
        <ShopLink piece={p} />
        <ReserveButton piece={p} {...actions} />
      </div>
    </article>
  );
}

function ReserveSheet({
  piece,
  onClose,
  onReserve,
}: {
  piece: SharedPiece;
  onClose: () => void;
  onReserve: (name: string) => void;
}) {
  const [name, setName] = useState('');
  return (
    <Sheet open onClose={onClose} onSubmit={() => onReserve(name.trim())} labelledBy="reserveTitle">
      <h2 id="reserveTitle">„{piece.name}“ reservieren</h2>
      <p className="hint">
        Andere sehen dann „Schon reserviert“. Du kannst es auf diesem Gerät wieder zurücknehmen. Deinen Namen sieht
        nur, wer den Link geteilt hat, und nur wenn Reservierungen dort eingeblendet sind.
      </p>
      <div className="field">
        <label htmlFor="r-name">Dein Name (optional)</label>
        <input
          id="r-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          autoComplete="given-name"
          data-autofocus
        />
      </div>
      <div className="sheet-actions">
        <div className="right">
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" className="btn ink">
            Reservieren
          </button>
        </div>
      </div>
    </Sheet>
  );
}
